
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UseDemoDetectionProps {
  showWarning?: boolean;
}

export const useDemoDetection = ({ showWarning = true }: UseDemoDetectionProps = {}) => {
  const { user } = useAuth();
  const [isDemo, setIsDemo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const checkDemoStatus = async () => {
      if (!user) {
        setIsDemo(false);
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Check if user has demo flag in user metadata
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        // Check if user is a demo account (either in custom column or in metadata)
        const isDemoAccount = 
          // Check custom column if it exists
          (data && 'is_demo' in data && data.is_demo === true) || 
          // Or check in user metadata
          (user.user_metadata && user.user_metadata.is_demo === true);
        
        setIsDemo(isDemoAccount);
        
        // If demo account and warnings are enabled, log a warning message
        if (isDemoAccount && showWarning) {
          console.warn('⚠️ Diese Session verwendet einen Demo-Account. Einige Funktionen sind eingeschränkt.');
        }
      } catch (error) {
        console.error('Error checking demo status:', error);
        setIsDemo(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkDemoStatus();
  }, [user, showWarning]);
  
  // Create a wrapper for actions that should be blocked in demo mode
  const demoGuard = (action: Function, fallback?: Function) => {
    return (...args: any[]) => {
      if (isDemo) {
        console.warn('⚠️ Diese Aktion ist im Demo-Modus nicht verfügbar.');
        if (fallback) {
          return fallback(...args);
        }
        return;
      }
      return action(...args);
    };
  };
  
  return {
    isDemo,
    isLoading,
    demoGuard,
    // Helper for demo UI indication
    demoLabel: isDemo ? '[DEMO]' : '',
  };
};

export default useDemoDetection;
